// O Fechamento de OS de um Lote, numa pergunta só (issue #90): a data e a
// hora dele — o gravado, ou o guardado enquanto a OS está reaberta para
// correção —, se está em correção e a chave de ordem do ADR-0028. Antes, cada
// lugar refazia `devolvidoEm ?? fechamentoAnteriorEm` à mão, e o
// cancelar/excluir ordenava diferente do "Fechar OS".

import type { DataISO } from "./datas.ts";
import type { Lote } from "./snapshot.ts";

/** O que a pergunta precisa de uma OS. */
export type LoteFechamento = Pick<
  Lote,
  | "id"
  | "coletaData"
  | "coletaHora"
  | "devolvidoEm"
  | "devolvidoHora"
  | "fechamentoAnteriorEm"
  | "fechamentoAnteriorHora"
  | "fechamentoRegistradoEm"
>;

export interface FechamentoDoLote {
  data: DataISO;
  /** Da mesma origem da data — nunca a hora do gravado com a data do anterior. */
  hora: string | null;
  /** Reaberta para correção: Lançada, com o Fechamento anterior guardado (não é uma Situação — ADR-0024). */
  emCorrecao: boolean;
}

/**
 * O Fechamento da OS: o gravado (`devolvidoEm`) ou, reaberta para correção, o
 * guardado (`fechamentoAnteriorEm`). `null` em OS sem nenhum dos dois.
 */
export function fechamentoDoLote(l: LoteFechamento): FechamentoDoLote | null {
  if (l.devolvidoEm != null) return { data: l.devolvidoEm, hora: l.devolvidoHora, emCorrecao: false };
  if (l.fechamentoAnteriorEm != null) return { data: l.fechamentoAnteriorEm, hora: l.fechamentoAnteriorHora, emCorrecao: true };
  return null;
}

/** OS reaberta para correção (Lançada com o Fechamento anterior guardado). */
export const emCorrecao = (l: LoteFechamento): boolean => fechamentoDoLote(l)?.emCorrecao ?? false;

/**
 * Chave de ordem cronológica de uma OS: o momento da **devolução** (data e
 * hora; sem hora — só em dado antigo — conta como início do dia). Não é a
 * ordem em que o fechamento foi gravado: os fechamentos entram no sistema
 * depois, em lote e fora de ordem (ADR-0028). Só no empate de data e hora
 * vale quando o Fechamento foi registrado no sistema pela primeira vez
 * (`fechamentoRegistradoEm`; sem ele, dado antigo, vem antes), e por fim a
 * coleta e o id — nunca dois iguais. OS reaberta usa a devolução guardada;
 * OS sem Fechamento, a coleta. `devolucao` substitui a gravada (o Fechamento
 * sendo digitado agora), e `registradoEm` o carimbo de uma OS que ainda não
 * tem um.
 */
export function momentoDevolucao(
  l: LoteFechamento,
  devolucao?: { data: string; hora?: string | null; registradoEm?: string | null },
): string {
  const gravado = fechamentoDoLote(l);
  const data = devolucao?.data ?? gravado?.data ?? l.coletaData;
  const hora = (devolucao ? devolucao.hora : gravado?.hora) ?? "";
  const registrado = l.fechamentoRegistradoEm ?? devolucao?.registradoEm ?? "";
  return `${data}|${hora.padEnd(5, " ")}|${registrado.padEnd(19, " ")}|${l.coletaData}|${l.coletaHora}|${l.id}`;
}

/** Carimbo de "ainda não registrado": ordena depois de qualquer registro já
 * feito — é o que um Fechamento ainda sendo digitado vai ganhar ao gravar. */
export const REGISTRO_PENDENTE = "~";
